import { Link } from "react-router-dom";
import { Award, Heart, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { TaglineMarquee } from "@/components/TaglineMarquee";
import { IMAGES } from "@/lib/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="The instructor behind the name"
        title="Meet Milly"
        italic="La Madrid"
        subtitle="Private swim instructor, founder of Mimi & Me Swimming, and the reason so many little swimmers fall in love with the water."
        image={IMAGES.millyWaveSelfie}
      />

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="space-y-6 font-body font-light text-lg leading-relaxed" style={{ color: "var(--muted)" }}>
                <p>The name "Mimi" came from the children I teach. Because my name has two L's, many
                  of them began calling me Mimi — and the name naturally became part of my business.</p>
                <p>I'm passionate about helping children become confident, capable and safe in the
                  water. My teaching style is loving, patient, structured, encouraging and
                  results-driven. I understand that children need time to build trust — especially
                  when they're working with a new instructor or learning in an unfamiliar environment.</p>
                <p>I believe that consistency, trust, repetition and teamwork between the instructor
                  and parents create the strongest results. I focus on much more than simply teaching
                  children how to swim: I work on breath control, body positioning, floating, safe
                  pool exits, independent swimming and lifelong water-safety skills.</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 space-y-5">
            {[{ i: Award, t: "American Red Cross Certified", d: "Committed to ongoing education in swimming, water safety, child development and emergency preparedness." }, { i: Heart, t: "Loving & Patient", d: "Every child is met exactly where they are, with warmth and care." }, { i: Sparkles, t: "Results-Driven", d: "Structured lessons designed to create real, lasting progress." }].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.1}>
                <div className="p-7 rounded-2xl bg-white border" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                  <c.i size={26} style={{ color: "var(--gold)" }} />
                  <h3 className="font-display text-2xl mt-4" style={{ color: "var(--navy)" }}>{c.t}</h3>
                  <p className="font-body font-light text-sm mt-2" style={{ color: "var(--muted)" }}>{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TaglineMarquee dark />

      {/* CERTIFICATIONS */}
      <section className="py-24" style={{ backgroundColor: "var(--sand)" }} data-testid="about-certifications">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border shadow-[0_30px_80px_rgba(11,27,61,0.15)]" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
              <img src={IMAGES.certSSU} alt="Superhero Swim University — SSU Online Survival Swim Certification awarded to Milagros La Madrid" className="w-full h-auto block" data-testid="cert-ssu-image" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="overline mb-4" style={{ color: "var(--gold)" }}>Certified &amp; trained</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05]" style={{ color: "var(--navy)" }}>
                Trained in <span className="italic">infant survival swim.</span>
              </h2>
              <p className="font-body font-light text-lg mt-6 leading-relaxed" style={{ color: "var(--muted)" }}>
                Milly holds the <strong>Superhero Swim University Online Survival Swim Certification</strong> —
                completing coursework in Infant Brain Development, Infant Survival Skills, and earning
                Level 2 SSU Member status. It's part of her ongoing commitment to the safest,
                most current methods for teaching little swimmers.
              </p>
              <ul className="mt-6 space-y-3">
                {["Infant Brain Development", "Infant Survival Skills", "Level 2 SSU Member", "American Red Cross Certified"].map((c) => (
                  <li key={c} className="flex items-center gap-3 font-body" style={{ color: "var(--navy)" }}>
                    <Award size={17} style={{ color: "var(--gold)" }} /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="overline mb-6" style={{ color: "var(--gold)" }}>My mission</p>
            <h2 className="font-display text-3xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
              To provide personalized, high-quality swim instruction that gives children
              <span className="italic"> confidence, safety, independence</span> and skills for life.
            </h2>
            <Link to="/contact" className="btn-primary mt-10">Work with Milly</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
